import { Fragment, useMemo, useState } from 'react';

import { fmt } from '@dcc-erp/shared';
import { Icon } from '@dcc-erp/ui';

import { DimensionFilter } from '../dimensions';
import {
  PREPAID_ASSET_CODE,
  PREPAID_DATE_FROM_DEFAULT,
  PREPAID_DATE_TO_DEFAULT,
  PP_CATS,
  EXPIRY_THRESHOLD_DAYS,
  getCat,
  isExpiringSoon,
  useDateMode,
  type CatCode,
  type PPItem,
} from './pp-helpers';

// P9 §3.7 — saved filter views (name + the filter axes, persisted to
// localStorage; no backend). Applying one drives the register's filter state.
interface PrepaidSavedView {
  name: string;
  q: string;
  catF: string;
  statusF: string;
  branchId: string;
  deptId: string;
  expSoon: boolean;
  dateFrom: string;
  dateTo: string;
}
const PREPAID_VIEWS_KEY = 'prepaid.savedViews';
function loadSavedViews(): PrepaidSavedView[] {
  try {
    const raw = localStorage.getItem(PREPAID_VIEWS_KEY);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    return Array.isArray(parsed) ? (parsed as PrepaidSavedView[]) : [];
  } catch {
    return [];
  }
}
function persistSavedViews(views: PrepaidSavedView[]): void {
  try {
    localStorage.setItem(PREPAID_VIEWS_KEY, JSON.stringify(views));
  } catch {
    /* localStorage unavailable (private mode) — saved views simply don't persist. */
  }
}
// Built-in presets, always offered ahead of any user-saved views.
const PREPAID_VIEW_PRESETS: readonly { id: string; name: string; view: PrepaidSavedView }[] = [
  {
    id: 'preset-active',
    name: 'กำลังตัดจ่าย',
    view: {
      name: 'กำลังตัดจ่าย',
      q: '',
      catF: 'all',
      statusF: 'active',
      branchId: '',
      deptId: '',
      expSoon: false,
      dateFrom: PREPAID_DATE_FROM_DEFAULT,
      dateTo: PREPAID_DATE_TO_DEFAULT,
    },
  },
  {
    id: 'preset-expiring',
    name: 'ใกล้ครบกำหนด',
    view: {
      name: 'ใกล้ครบกำหนด',
      q: '',
      catF: 'all',
      statusF: 'all',
      branchId: '',
      deptId: '',
      expSoon: true,
      dateFrom: PREPAID_DATE_FROM_DEFAULT,
      dateTo: PREPAID_DATE_TO_DEFAULT,
    },
  },
  {
    id: 'preset-pending',
    name: 'ยังไม่เริ่ม',
    view: {
      name: 'ยังไม่เริ่ม',
      q: '',
      catF: 'all',
      statusF: 'pending',
      branchId: '',
      deptId: '',
      expSoon: false,
      dateFrom: PREPAID_DATE_FROM_DEFAULT,
      dateTo: PREPAID_DATE_TO_DEFAULT,
    },
  },
];

export interface RegisterProps {
  rows: readonly PPItem[];
  items: readonly PPItem[];
  q: string;
  setQ: (s: string) => void;
  catF: string;
  setCatF: (s: string) => void;
  statusF: string;
  setStatusF: (s: string) => void;
  expSoon: boolean;
  setExpSoon: (v: boolean) => void;
  dateFrom: string;
  setDateFrom: (s: string) => void;
  dateTo: string;
  setDateTo: (s: string) => void;
  companyId: string | undefined;
  filterBranchId: string;
  setFilterBranchId: (id: string) => void;
  filterDepartmentId: string;
  setFilterDepartmentId: (id: string) => void;
  onPick: (r: PPItem) => void;
}

export function PPRegister({
  rows,
  items,
  q,
  setQ,
  catF,
  setCatF,
  statusF,
  setStatusF,
  expSoon,
  setExpSoon,
  dateFrom,
  setDateFrom,
  dateTo,
  setDateTo,
  companyId,
  filterBranchId,
  setFilterBranchId,
  filterDepartmentId,
  setFilterDepartmentId,
  onPick,
}: RegisterProps) {
  const { mode, setMode, df } = useDateMode();
  // P9 §9.8 — vendor rollup: group the (already-filtered) rows by ผู้ขาย into a
  // parent row (Σ value / Σ monthly / Σ remaining / active+expiring counts) with
  // expand-to-members. Pure client-side aggregation; no backend.
  const [groupByVendor, setGroupByVendor] = useState<boolean>(false);
  const [expandedVendors, setExpandedVendors] = useState<ReadonlySet<string>>(new Set());
  const toggleVendor = (name: string): void =>
    setExpandedVendors((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  const vendorGroups = useMemo(() => {
    const map = new Map<string, PPItem[]>();
    for (const r of rows) {
      const key = r.vendor || '— ไม่ระบุผู้ขาย —';
      const arr = map.get(key);
      if (arr) arr.push(r);
      else map.set(key, [r]);
    }
    return [...map.entries()]
      .map(([name, members]) => ({
        name,
        members,
        total: members.reduce((s, m) => s + m.total, 0),
        monthly: members.reduce((s, m) => s + (m.status === 'active' ? m.monthly : 0), 0),
        remaining: members.reduce((s, m) => s + m.monthly * m.remain, 0),
        activeCount: members.filter((m) => m.status === 'active').length,
        expiringCount: members.filter((m) => isExpiringSoon(m)).length,
      }))
      .sort((a, b) => b.total - a.total);
  }, [rows]);

  // P9 §3.7 — saved filter views (persisted) + the inline "save current view" UI.
  const [savedViews, setSavedViews] = useState<PrepaidSavedView[]>(() => loadSavedViews());
  const [savingView, setSavingView] = useState<boolean>(false);
  const [newViewName, setNewViewName] = useState<string>('');
  const applyView = (v: PrepaidSavedView): void => {
    setQ(v.q);
    setCatF(v.catF);
    setStatusF(v.statusF);
    setExpSoon(v.expSoon);
    setFilterBranchId(v.branchId);
    setFilterDepartmentId(v.deptId);
    // Reset the date range too so every applied view is deterministic across ALL
    // axes (older persisted views predate these fields → fall back to defaults).
    setDateFrom(v.dateFrom ?? PREPAID_DATE_FROM_DEFAULT);
    setDateTo(v.dateTo ?? PREPAID_DATE_TO_DEFAULT);
  };
  const saveCurrentView = (): void => {
    const name = newViewName.trim();
    if (!name) return;
    const view: PrepaidSavedView = {
      name,
      q,
      catF,
      statusF,
      expSoon,
      branchId: filterBranchId,
      deptId: filterDepartmentId,
      dateFrom,
      dateTo,
    };
    // Replace a same-named view rather than duplicating it.
    const next = [...savedViews.filter((v) => v.name !== name), view];
    setSavedViews(next);
    persistSavedViews(next);
    setNewViewName('');
    setSavingView(false);
  };
  const deleteView = (name: string): void => {
    const next = savedViews.filter((v) => v.name !== name);
    setSavedViews(next);
    persistSavedViews(next);
  };

  // One contract <tr>. Extracted so the §9.8 vendor-rollup expanded members can
  // reuse the exact same row (indented) as the flat register.
  const renderContractRow = (r: PPItem, indent = false) => {
    const cat = getCat(r.cat);
    const pct = Math.round((r.used / r.period) * 100);
    return (
      <tr key={r.id} onClick={() => onPick(r)} style={{ cursor: 'pointer' }}>
        <td onClick={(e) => e.stopPropagation()}>
          <input type="checkbox" />
        </td>
        <td className="code" style={indent ? { paddingLeft: 26 } : undefined}>
          <a style={{ color: 'var(--brand)', fontWeight: 500 }}>{r.id}</a>
          {(r.missingEvidenceCount ?? 0) > 0 && (
            <div
              title="ต้องแนบเอกสารก่อนตัดจ่ายงวดแรก"
              style={{
                marginTop: 3,
                display: 'inline-block',
                fontSize: 11,
                fontWeight: 600,
                color: 'var(--warning,#b4780a)',
                background: 'rgba(180,120,10,0.12)',
                borderRadius: 4,
                padding: '1px 6px',
              }}
            >
              ⚠ ขาดเอกสาร {r.missingEvidenceCount} รายการ
            </div>
          )}
        </td>
        <td>
          <div style={{ fontWeight: 500 }}>{r.vendor}</div>
          <div className="meta" style={{ marginTop: 1 }}>
            {r.desc}
          </div>
        </td>
        <td>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7 }}>
            <span style={{ width: 9, height: 9, borderRadius: 2, background: cat.tone }} />
            {cat.label}
          </span>
        </td>
        <td className="num tnum">{fmt(r.total)}</td>
        <td className="num">{r.period} เดือน</td>
        <td className="num tnum">{fmt(r.monthly)}</td>
        <td>{df(r.start)}</td>
        <td>{df(r.end)}</td>
        <td>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div
              style={{
                flex: 1,
                height: 6,
                borderRadius: 3,
                background: '#f0f0f3',
                overflow: 'hidden',
              }}
            >
              <div
                style={{
                  width: `${pct}%`,
                  height: '100%',
                  background: pct >= 100 ? 'var(--positive)' : cat.tone,
                }}
              />
            </div>
            <span
              style={{
                fontSize: 14,
                color: 'var(--text-muted)',
                minWidth: 40,
                textAlign: 'right',
              }}
            >
              {r.used}/{r.period}
            </span>
          </div>
        </td>
        <td className="num tnum">
          <b>{fmt(r.monthly * r.remain)}</b>
        </td>
        <td>
          {r.apiStatus === 'SUSPENDED' ? (
            <span className="badge warning">
              <span className="bdot" />
              พักสัญญา
            </span>
          ) : r.apiStatus === 'TERMINATED' ? (
            <span className="badge neutral">
              <span className="bdot" />
              ยกเลิกแล้ว
            </span>
          ) : r.status === 'pending' ? (
            <span className="badge warning">
              <span className="bdot" />
              ยังไม่เริ่ม
            </span>
          ) : r.status === 'completed' ? (
            <span className="badge positive">
              <span className="bdot" />
              ครบแล้ว
            </span>
          ) : (
            <span className="badge brand">
              <span className="bdot" />
              กำลังตัดจ่าย
            </span>
          )}
        </td>
      </tr>
    );
  };

  // P9 — active-filter chips (each removable). Date-range is treated as one chip.
  const chips: { key: string; label: string; clear: () => void }[] = [];
  if (q) chips.push({ key: 'q', label: `ค้นหา: "${q}"`, clear: () => setQ('') });
  if (catF !== 'all')
    chips.push({
      key: 'cat',
      label: `หมวด: ${getCat(catF as CatCode).label}`,
      clear: () => setCatF('all'),
    });
  if (statusF !== 'all') {
    const sl =
      statusF === 'pending' ? 'ยังไม่เริ่ม' : statusF === 'active' ? 'กำลังตัดจ่าย' : 'ตัดจ่ายครบ';
    chips.push({ key: 'status', label: `สถานะ: ${sl}`, clear: () => setStatusF('all') });
  }
  if (filterBranchId)
    chips.push({ key: 'branch', label: 'สาขาที่เลือก', clear: () => setFilterBranchId('') });
  if (filterDepartmentId)
    chips.push({ key: 'dept', label: 'แผนกที่เลือก', clear: () => setFilterDepartmentId('') });
  if (expSoon)
    chips.push({
      key: 'expSoon',
      label: `ใกล้ครบกำหนด (${EXPIRY_THRESHOLD_DAYS} วัน)`,
      clear: () => setExpSoon(false),
    });
  const clearAll = () => {
    setQ('');
    setCatF('all');
    setStatusF('all');
    setExpSoon(false);
    setFilterBranchId('');
    setFilterDepartmentId('');
  };

  return (
    <div className="tbl-shell">
      <div className="filter-row">
        <input
          className="input search w-lg"
          placeholder="ค้นหา เลขที่ / ผู้ขาย / รายละเอียด"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          style={{
            paddingLeft: 32,
            backgroundImage:
              "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='14' height='14' viewBox='0 0 24 24' fill='none' stroke='%238a8a93' stroke-width='2'%3E%3Ccircle cx='11' cy='11' r='7'/%3E%3Cpath d='M21 21l-4.3-4.3'/%3E%3C/svg%3E\")",
            backgroundRepeat: 'no-repeat',
            backgroundPosition: '10px center',
          }}
        />
        <select className="select" value={catF} onChange={(e) => setCatF(e.target.value)}>
          <option value="all">หมวด: ทั้งหมด</option>
          {PP_CATS.map((c) => (
            <option key={c.code} value={c.code}>
              {c.label}
            </option>
          ))}
        </select>
        <select className="select" value={statusF} onChange={(e) => setStatusF(e.target.value)}>
          <option value="all">สถานะ: ทั้งหมด</option>
          <option value="pending">ยังไม่เริ่ม</option>
          <option value="active">กำลังตัดจ่าย</option>
          <option value="completed">ตัดจ่ายครบ</option>
        </select>
        <DimensionFilter
          companyId={companyId}
          branchId={filterBranchId}
          departmentId={filterDepartmentId}
          onBranchChange={setFilterBranchId}
          onDepartmentChange={setFilterDepartmentId}
        />
        <input
          className="input w-sm"
          type="date"
          value={dateFrom}
          onChange={(e) => setDateFrom(e.target.value)}
        />
        <span className="faint">ถึง</span>
        <input
          className="input w-sm"
          type="date"
          value={dateTo}
          onChange={(e) => setDateTo(e.target.value)}
        />
        <div style={{ flex: 1 }} />
        {/* P9 §9.6 — พ.ศ. / ค.ศ. date display toggle (per-user, all prepaid surfaces). */}
        <button
          className="btn sm ghost"
          title="สลับการแสดงปีระหว่าง พ.ศ. และ ค.ศ."
          onClick={() => setMode(mode === 'BE' ? 'CE' : 'BE')}
        >
          ปี: {mode === 'BE' ? 'พ.ศ.' : 'ค.ศ.'}
        </button>
      </div>
      {/* P9 §3.7 — saved views bar: built-in presets + user-saved views (apply /
          delete / save-current) on the left, the §9.8 vendor-rollup toggle right. */}
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 6,
          alignItems: 'center',
          padding: '8px 2px 0',
        }}
      >
        <span className="faint" style={{ fontSize: 13 }}>
          มุมมอง:
        </span>
        {PREPAID_VIEW_PRESETS.map((p) => (
          <button key={p.id} className="btn sm ghost" onClick={() => applyView(p.view)}>
            {p.name}
          </button>
        ))}
        {savedViews.map((v) => (
          <span
            key={v.name}
            className="badge"
            style={{ border: '1px solid var(--border)', gap: 4, paddingRight: 4 }}
          >
            <button
              onClick={() => applyView(v)}
              title="ใช้มุมมองนี้"
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                color: 'var(--brand)',
                font: 'inherit',
                padding: 0,
              }}
            >
              {v.name}
            </button>
            <button
              onClick={() => deleteView(v.name)}
              title="ลบมุมมอง"
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                padding: 0,
                display: 'inline-flex',
              }}
            >
              <Icon name="x" size={10} />
            </button>
          </span>
        ))}
        {savingView ? (
          <span style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
            <input
              className="input w-sm"
              placeholder="ตั้งชื่อมุมมอง"
              value={newViewName}
              onChange={(e) => setNewViewName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') saveCurrentView();
                if (e.key === 'Escape') {
                  setSavingView(false);
                  setNewViewName('');
                }
              }}
              autoFocus
            />
            <button
              className="btn sm primary"
              onClick={saveCurrentView}
              disabled={!newViewName.trim()}
            >
              บันทึก
            </button>
            <button
              className="btn sm ghost"
              onClick={() => {
                setSavingView(false);
                setNewViewName('');
              }}
            >
              ยกเลิก
            </button>
          </span>
        ) : (
          <button
            className="btn sm ghost"
            title="บันทึกตัวกรองปัจจุบันเป็นมุมมอง"
            onClick={() => setSavingView(true)}
          >
            ＋ บันทึกมุมมอง
          </button>
        )}
        <div style={{ flex: 1 }} />
        <button
          className="btn sm ghost"
          aria-pressed={groupByVendor}
          title="รวมสัญญาของผู้ขายรายเดียวกันเป็นแถวเดียว"
          onClick={() => setGroupByVendor((v) => !v)}
          style={
            groupByVendor
              ? { background: 'var(--brand)', color: 'white', borderColor: 'var(--brand)' }
              : undefined
          }
        >
          จัดกลุ่มตามผู้ขาย
        </button>
      </div>
      {/* P9 — active filter chips (removable). */}
      {chips.length > 0 && (
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 6,
            alignItems: 'center',
            padding: '6px 2px 2px',
          }}
        >
          {chips.map((c) => (
            <button
              key={c.key}
              className="badge"
              onClick={c.clear}
              style={{ cursor: 'pointer', border: '1px solid var(--border)', gap: 6 }}
              title="ลบตัวกรองนี้"
            >
              {c.label}
              <Icon name="x" size={10} />
            </button>
          ))}
          <button className="btn sm ghost" onClick={clearAll}>
            ล้างทั้งหมด
          </button>
        </div>
      )}
      <div className="tbl-wrap" style={{ maxHeight: 560 }}>
        <table className="tbl">
          <thead>
            <tr>
              <th style={{ width: 24 }}>
                <input type="checkbox" />
              </th>
              <th style={{ width: 140 }}>เลขที่</th>
              <th>ผู้ขาย / รายละเอียด</th>
              <th>หมวด</th>
              <th className="num">มูลค่าสัญญา</th>
              <th className="num">งวดตัดจ่าย</th>
              <th className="num">ตัดจ่าย/เดือน</th>
              <th>เริ่มตัดจ่าย</th>
              <th>สิ้นสุด</th>
              <th style={{ width: 160 }}>ความคืบหน้า</th>
              <th className="num">คงเหลือ</th>
              <th>สถานะ</th>
            </tr>
          </thead>
          <tbody>
            {groupByVendor
              ? vendorGroups.map((g) => {
                  const open = expandedVendors.has(g.name);
                  return (
                    <Fragment key={g.name}>
                      <tr
                        onClick={() => toggleVendor(g.name)}
                        style={{
                          cursor: 'pointer',
                          background: 'rgba(0,0,0,0.025)',
                          fontWeight: 600,
                        }}
                      >
                        <td style={{ textAlign: 'center' }}>{open ? '▾' : '▸'}</td>
                        <td className="code">{g.members.length} สัญญา</td>
                        <td>
                          <div style={{ fontWeight: 600 }}>{g.name}</div>
                          <div className="meta" style={{ marginTop: 1 }}>
                            กำลังตัดจ่าย {g.activeCount}
                            {g.expiringCount > 0 ? ` · ใกล้ครบ ${g.expiringCount}` : ''}
                          </div>
                        </td>
                        <td />
                        <td className="num tnum">{fmt(g.total)}</td>
                        <td />
                        <td className="num tnum">{fmt(g.monthly)}</td>
                        <td />
                        <td />
                        <td>
                          {g.expiringCount > 0 && (
                            <span className="badge warning">
                              <span className="bdot" />
                              ใกล้ครบ {g.expiringCount}
                            </span>
                          )}
                        </td>
                        <td className="num tnum">
                          <b>{fmt(g.remaining)}</b>
                        </td>
                        <td />
                      </tr>
                      {open && g.members.map((m) => renderContractRow(m, true))}
                    </Fragment>
                  );
                })
              : rows.map((r) => renderContractRow(r))}
            <tr className="total">
              <td colSpan={4}>รวม {rows.length} สัญญา</td>
              <td className="num tnum">{fmt(rows.reduce((s, r) => s + r.total, 0))}</td>
              <td />
              <td className="num tnum">{fmt(rows.reduce((s, r) => s + r.monthly, 0))}</td>
              <td colSpan={3} />
              <td className="num tnum">
                {fmt(rows.reduce((s, r) => s + r.monthly * r.remain, 0))}
              </td>
              <td />
            </tr>
          </tbody>
        </table>
      </div>
      <div className="tbl-footer">
        <span>
          แสดง {rows.length} จาก {items.length} สัญญา · ลงบัญชี {PREPAID_ASSET_CODE} —
          ค่าใช้จ่ายจ่ายล่วงหน้า · คลิกที่แถวเพื่อดูตารางตัดจ่าย
        </span>
      </div>
    </div>
  );
}
